import { useMemo } from "react";

// React Query
import { InfiniteData, useInfiniteQuery, UseInfiniteQueryOptions } from "@tanstack/react-query";

// Types
import { IBasicPaginatedQueryResponse } from "@/Common/api/types/pagination";

// Helpers
import { buildGetNextPageParam, getAllPaginatedItems } from "./helpers";

interface IUsePaginatedResourceConfiguration<T>
	extends UseInfiniteQueryOptions<IBasicPaginatedQueryResponse<T>, Error, InfiniteData<IBasicPaginatedQueryResponse<T>>> {
	fetchAllPages?: boolean;
}

export function useBasicPaginatedResource<T>(configuration: IUsePaginatedResourceConfiguration<T>) {
	const { fetchAllPages, ...infiniteQueryConfiguration } = configuration;

	const infiniteQueryResult = useInfiniteQuery({
		...infiniteQueryConfiguration,
		getNextPageParam: configuration.getNextPageParam ?? buildGetNextPageParam(),
	});

	const data = useMemo(() => getAllPaginatedItems(infiniteQueryResult.data), [infiniteQueryResult.data]);

	if (fetchAllPages && !infiniteQueryResult.isFetching && !infiniteQueryResult.isLoading && infiniteQueryResult.hasNextPage) {
		infiniteQueryResult.fetchNextPage({ cancelRefetch: false });
	}

	return { ...infiniteQueryResult, paginatedData: infiniteQueryResult.data, data };
}
